import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import LockIcon from '@mui/icons-material/Lock'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'
import React from 'react'

import type { SchemaQuota, SchemaQuotaAdd, SchemaQuotaEdit } from '@/__generated/es-api'
import Button from '@/app/component/atom/button'
import IconButton from '@/app/component/atom/icon-button'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import type { CampaignItem } from '@/app/module/campaigns/types'
import type { Condition, PersonalizationType } from '@/app/types'

import QuotaAddButton from './quota-add-button'
import QuotaCreateDialog from './quota-create-dialog'
import QuotaEditDialog from './quota-edit-dialog'
import QuotaLibraryDialog from './quota-library-dialog'

type BaseProps = {
  campaignCountsByQuotaId: Record<string, number>
  item: CampaignItem
}

type EditableProps = BaseProps & {
  isCreatingQuota: boolean
  isFetchingQuotas: boolean
  isUpdatingQuota: boolean
  personalizationList: PersonalizationType
  readOnly?: false
  onAddOrganizationQuota: (quota: SchemaQuota) => boolean
  onCreateQuota: (quota: SchemaQuotaAdd, conditions: Condition | null) => Promise<boolean>
  onDeleteQuota: (quotaId: string) => void
  onSaveConditions: (quotaId: string, conditions: Condition | null) => void
  onSaveMetadata: (quotaId: string, quota: Required<SchemaQuotaEdit>) => Promise<boolean>
  onSaveQuota: (quotaId: string, quota: Required<SchemaQuotaEdit>, conditions: Condition | null) => Promise<boolean>
}

type ReadOnlyProps = BaseProps & {
  readOnly: true
}

type Props = EditableProps | ReadOnlyProps

const getQuotaPercentage = (used: number, goal: number): number => {
  if (!Number.isFinite(used) || !Number.isFinite(goal) || goal <= 0) {
    return 0
  }

  return Math.min(100, Math.max(0, Math.round((used / goal) * 100)))
}

const formatQuotaNumber = (value: number): string => (Number.isFinite(value) ? String(value) : '—')

const Quotas: React.FC<Props> = (props) => {
  const { campaignCountsByQuotaId, item } = props
  const onAddOrganizationQuota = props.readOnly === true ? undefined : props.onAddOrganizationQuota
  const onDeleteQuota = props.readOnly === true ? undefined : props.onDeleteQuota
  const createConfirmation = useConfirmation()
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false)
  const [duplicatingQuotaId, setDuplicatingQuotaId] = React.useState<string | undefined>()
  const [editingQuotaId, setEditingQuotaId] = React.useState<string | undefined>()
  const [locallyAddedQuotasById, setLocallyAddedQuotasById] = React.useState<Record<string, SchemaQuota>>({})
  const [quotaLibraryDialogOpen, setQuotaLibraryDialogOpen] = React.useState(false)
  const duplicatingQuota = React.useMemo(
    () => item.quotas?.find(({ quotaId }) => quotaId === duplicatingQuotaId),
    [duplicatingQuotaId, item.quotas],
  )
  const editingQuota = React.useMemo(
    () => item.quotas?.find(({ quotaId }) => quotaId === editingQuotaId),
    [editingQuotaId, item.quotas],
  )
  const currentCampaignIsLive = item.launched && item.published

  const getCampaignCount = React.useCallback(
    (quotaId: string) => {
      const locallyAddedQuota = locallyAddedQuotasById[quotaId]
      const serverCampaignCount = campaignCountsByQuotaId[quotaId]

      if (
        locallyAddedQuota &&
        (serverCampaignCount === undefined || serverCampaignCount === locallyAddedQuota.campaigns)
      ) {
        return locallyAddedQuota.campaigns + Number(currentCampaignIsLive)
      }

      return serverCampaignCount ?? 0
    },
    [campaignCountsByQuotaId, currentCampaignIsLive, locallyAddedQuotasById],
  )

  const handleAddOrganizationQuota = React.useCallback(
    (quota: SchemaQuota) => {
      if (!onAddOrganizationQuota?.(quota)) {
        return
      }

      setLocallyAddedQuotasById((quotasById) => ({ ...quotasById, [quota.id]: quota }))
      setQuotaLibraryDialogOpen(false)
      setEditingQuotaId(quota.id)
    },
    [onAddOrganizationQuota],
  )

  const handleDeleteQuota = React.useCallback(
    async (quotaId: string, quotaName: string) => {
      if (!onDeleteQuota) {
        return
      }

      const confirmed = await createConfirmation({
        cancelButtonLabel: 'Cancel',
        confirmButtonLabel: 'Remove',
        content: (
          <Box>
            <Typography>
              Remove <Box component="strong">{quotaName}</Box> from this campaign? The quota will remain in your
              account.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              The quota will also remain connected to any other campaigns it is linked to.
            </Typography>
          </Box>
        ),
        icon: 'delete',
        title: 'Remove quota?',
      })

      if (confirmed) {
        setLocallyAddedQuotasById((quotasById) =>
          Object.fromEntries(Object.entries(quotasById).filter(([id]) => id !== quotaId)),
        )
        onDeleteQuota(quotaId)
      }
    },
    [createConfirmation, onDeleteQuota],
  )

  return (
    <>
      <Card sx={{ mb: 4 }}>
        <CardHeader
          action={
            !props.readOnly ? (
              <QuotaAddButton
                onAddExistingQuota={() => setQuotaLibraryDialogOpen(true)}
                onCreateQuota={() => setCreateDialogOpen(true)}
              />
            ) : undefined
          }
          slotProps={{
            action: { style: { alignSelf: 'center', marginBottom: 0, marginRight: 0, marginTop: 0 } },
          }}
          title="Quotas"
          sx={{ px: 3, py: 1.5 }}
        />
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pt: 1 }}>
          <Typography color="text.secondary" variant="body2">
            {props.readOnly
              ? 'See here how many surveys were completed for each quota. Edit the campaign to change the quotas.'
              : 'Set targets for how many completed responses you want from specific groups of contacts. Quotas are saved to your account and can be reused across campaigns.'}
          </Typography>
          {item.quotas?.map((quota) => {
            const campaignCount = getCampaignCount(quota.quotaId)
            const hasConditions = Boolean(quota.conditions?.length)
            const percentage = getQuotaPercentage(quota.quotaUsed, quota.quotaGoal)

            return (
              <Box
                data-quota-status={quota.quotaHit ? 'reached' : 'active'}
                data-testid={`quota-card-${quota.quotaId}`}
                key={quota.quotaId}
                sx={{
                  bgcolor: 'action.hover',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2.25,
                  py: 2,
                }}
              >
                <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                  <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: '9px' }}>
                    <Typography component="h3" data-testid="quota-name" fontWeight={600} variant="subtitle1">
                      {quota.quotaName}
                    </Typography>
                    {campaignCount > 1 && (
                      <Box
                        component="span"
                        data-testid={`quota-shared-badge-${quota.quotaId}`}
                        sx={{
                          alignItems: 'center',
                          bgcolor: '#e9ebed',
                          borderRadius: '3px',
                          color: '#7a828a',
                          display: 'inline-flex',
                          fontSize: '11.5px',
                          gap: '5px',
                          px: '8px',
                          py: '2px',
                        }}
                      >
                        <LockIcon sx={{ fontSize: '10px' }} />
                        Shared · used in {campaignCount} campaigns
                      </Box>
                    )}
                  </Box>
                  {!props.readOnly && (
                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                      <IconButton
                        aria-label={`Edit quota ${quota.quotaName}`}
                        disabled={props.isFetchingQuotas}
                        tooltip="Edit quota"
                        onClick={() => setEditingQuotaId(quota.quotaId)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label={`Duplicate quota ${quota.quotaName}`}
                        tooltip="Duplicate quota"
                        onClick={() => setDuplicatingQuotaId(quota.quotaId)}
                      >
                        <ContentCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label={`Remove quota ${quota.quotaName}`}
                        tooltip="Remove quota"
                        onClick={() => handleDeleteQuota(quota.quotaId, quota.quotaName)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    alignItems: { sm: 'center' },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 0.75, sm: 2 },
                    mt: 1.5,
                  }}
                >
                  <LinearProgress
                    aria-label={`${quota.quotaName} progress`}
                    aria-valuetext={`${formatQuotaNumber(quota.quotaUsed)} of ${formatQuotaNumber(quota.quotaGoal)} (${percentage}%)`}
                    color={quota.quotaHit ? 'success' : 'primary'}
                    sx={{
                      backgroundColor: '#e3e6e9',
                      borderRadius: '5px',
                      height: 8,
                      maxWidth: 340,
                      width: '100%',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: '5px',
                      },
                    }}
                    value={percentage}
                    variant="determinate"
                  />
                  <Typography color="text.secondary" variant="body2">
                    {formatQuotaNumber(quota.quotaUsed)} / {formatQuotaNumber(quota.quotaGoal)} ({percentage}%)
                  </Typography>
                </Box>
                <Box sx={{ alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                  <Typography color="text.secondary" variant="body2">
                    {hasConditions
                      ? 'Only some completed surveys count towards this quota.'
                      : 'Every completed survey counts towards this quota.'}
                  </Typography>
                  {!props.readOnly && (
                    <Button
                      disabled={props.isFetchingQuotas}
                      size="small"
                      sx={{ minWidth: 0, p: 0, textTransform: 'none' }}
                      onClick={() => setEditingQuotaId(quota.quotaId)}
                    >
                      {hasConditions ? 'Change by editing conditions' : 'Change by adding conditions'}
                    </Button>
                  )}
                </Box>
              </Box>
            )
          })}
        </CardContent>
      </Card>
      {!props.readOnly && createDialogOpen && (
        <QuotaCreateDialog
          isCreating={props.isCreatingQuota}
          personalizationList={props.personalizationList}
          onClose={() => setCreateDialogOpen(false)}
          onCreate={props.onCreateQuota}
        />
      )}
      {!props.readOnly && quotaLibraryDialogOpen && (
        <QuotaLibraryDialog
          addedQuotaIds={item.quotas?.map(({ quotaId }) => quotaId) ?? []}
          onAddQuota={handleAddOrganizationQuota}
          onClose={() => setQuotaLibraryDialogOpen(false)}
        />
      )}
      {!props.readOnly && duplicatingQuota && (
        <QuotaCreateDialog
          isCreating={props.isCreatingQuota}
          key={duplicatingQuota.quotaId}
          personalizationList={props.personalizationList}
          referenceQuota={duplicatingQuota}
          onClose={() => setDuplicatingQuotaId(undefined)}
          onCreate={props.onCreateQuota}
        />
      )}
      {!props.readOnly && editingQuota && (
        <QuotaEditDialog
          campaignCount={getCampaignCount(editingQuota.quotaId)}
          isUpdating={props.isUpdatingQuota}
          key={editingQuota.quotaId}
          personalizationList={props.personalizationList}
          quota={editingQuota}
          onClose={() => setEditingQuotaId(undefined)}
          onSaveConditions={props.onSaveConditions}
          onSaveMetadata={props.onSaveMetadata}
          onSaveQuota={props.onSaveQuota}
        />
      )}
    </>
  )
}

export default Quotas
